import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from './useDebounce';
import { normalize } from '@/utils/textUtils';

export interface Settlement {
  name: string;
  name_en?: string;
  name_fr?: string;
  name_ru?: string;
  latitude: number | null;
  longitude: number | null;
}

export const useSettlements = (searchTerm: string) => {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (debouncedSearch.length < 2) {
      setSettlements([]);
      setLoading(false);
      return;
    }

    const fetchSettlements = async () => {
      setLoading(true);
      try {
        // Normalize search term for fuzzy matching
        const normalizedSearch = normalize(debouncedSearch);

        // Fetch settlements with translation columns
        const { data, error } = await supabase
          .from('israeli_settlements')
          .select('settlement_name, settlement_name_latin, settlement_name_en, settlement_name_fr, settlement_name_ru, latitude, longitude')
          .or(`settlement_name.ilike.%${debouncedSearch}%,settlement_name_latin.ilike.%${debouncedSearch}%,settlement_name_en.ilike.%${debouncedSearch}%,settlement_name_fr.ilike.%${debouncedSearch}%,settlement_name_ru.ilike.%${debouncedSearch}%`)
          .order('settlement_name')
          .limit(100);

        if (!error && data) {
          // Client-side fuzzy filter for better matching (handles accents, spaces, etc.)
          const filtered = data.filter(s => {
            const variants = [
              s.settlement_name,
              s.settlement_name_latin,
              s.settlement_name_en,
              s.settlement_name_fr,
              s.settlement_name_ru
            ].filter(Boolean);

            return variants.some(v => normalize(v!).includes(normalizedSearch));
          });

          const settlements = filtered.map(s => ({
            name: s.settlement_name,
            name_en: s.settlement_name_en,
            name_fr: s.settlement_name_fr,
            name_ru: s.settlement_name_ru,
            latitude: s.latitude,
            longitude: s.longitude
          }));
          setSettlements(settlements);
        } else {
          setSettlements([]);
        }
      } catch (error) {
        console.error('Error fetching settlements:', error);
        setSettlements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSettlements();
  }, [debouncedSearch]);

  return { settlements, loading };
};
